// Punktlista (formerly Rapportvakt) — data for the standalone watch service,
// now hosted on its own domain (punktlista.se) instead of a route under
// utforskadata.se.
//
// Plain ESM with no JSX and no Vite-only syntax: scripts/prerender-seo.mjs
// imports src/config/seoRoutes.js in bare Node, and that file imports this one.

// digests.js is itself generated (by scripts/export_digests.py) rather than
// hand-authored, but it is a plain ESM module for the same reason this file
// is: web/scripts/prerender-seo.mjs and web/scripts/generate-sitemap.mjs load
// it in bare Node with no build step. See that script's own file header for
// why it is a .js module and not a JSON import.
import { RAPPORTVAKT_DIGESTS } from './digests.js';
//
// EDITORIAL CONTRACT
// -----------------
// Every figure, page number, section name and URL below is copied verbatim from
// the source notes in ~/code/active/rapportvakt/samples/*.md. Prose may be
// re-cut and shortened; numbers may never be altered, rounded, recomputed or
// invented. That includes derived numbers: a gap between two figures is drawn
// as a gap, never printed as a subtraction the report did not publish.
//
// The takeaway model is deliberately not a paragraph. Each point is:
//   headline  — blunt claim, 4 to 9 words, grasped in about two seconds
//   hero      — the single dominant figure, with its own space
//   visual    — the comparison, drawn (columns / bars / gap / delta / split / steps)
//   facts     — secondary figures as a scannable strip, never prose
//   detail    — the rest, behind "visa mer"
//   citation  — page, section and a deep link into the PDF. Never buried.

export const RAPPORTVAKT_APP_PATH = '/app';
export const RAPPORTVAKT_LANDING_PATH = '/';

/**
 * /om — the About page, and the one place a reader is sent with a question.
 *
 * Every surface that used to print the contact address links here instead, so
 * the address is written down once (in the footer's contact column and behind
 * the about page's own mailto button) rather than scattered across sheets and
 * CTAs where it reads as a support desk that does not exist.
 *
 * Was /hjalp until the 2026-09 rename to About; /hjalp/* now 301s to /om/
 * (see netlify.toml). Trailing slash: the build prerenders this as
 * dist/om/index.html, which Netlify serves at /om/ and 301s /om (no slash)
 * to. Every path below that becomes a prerendered directory carries the same
 * trailing slash, so the sitemap, the canonical tag and the URL Netlify
 * actually serves are always the same string. /app is the one exception: it
 * stays without a slash because it is also the prefix rapportvaktTabPath
 * builds /app/<tab> from.
 */
export const RAPPORTVAKT_HELP_PATH = '/om/';

/**
 * /logga-in — where a mailed login link lands (docs/plan/2026-09-11-magic-
 * link-login.md). Not a page a reader is ever sent to on their own; only
 * reachable via a link from an email, or the sign-in forms elsewhere on the
 * site. See src/config/seoRoutes.js's getRapportvaktLoginSeo for why it
 * carries noindex/nofollow and stays out of the sitemap despite still
 * getting a prerendered dist/logga-in/index.html (so a hard reload works).
 */
export const RAPPORTVAKT_LOGIN_PATH = '/logga-in/';

/** The founder photo on the help page. 600x800, served locally. */
export const RAPPORTVAKT_FOUNDER_PHOTO = '/osama.jpg';

/**
 * The two surfaces the app shows as tabs, each with its own URL so a tab can be
 * linked, bookmarked and reloaded.
 *
 * The segments are Swedish because the whole product is; the id is what the
 * page and the string table key off. /app itself renders the first
 * one, and only /app is indexed — the tab URLs canonicalise back
 * to it, see getRapportvaktAppSeo in src/config/seoRoutes.js.
 */
export const RAPPORTVAKT_APP_TABS = [
  { id: 'digest', segment: 'utskick' },
  { id: 'bevakningar', segment: 'bevakningar' },
];

/**
 * Every app route. Din plan is one of them but is not a tab: it is account
 * admin, not a place you work, so it sits in the top bar next to Hjälp on every
 * Punktlista page instead of taking a third of the app's tab row. The route
 * itself is unchanged, which is why it belongs in this list and not the one
 * above.
 */
export const RAPPORTVAKT_APP_ROUTES = [
  ...RAPPORTVAKT_APP_TABS,
  { id: 'plan', segment: 'plan' },
];

export function rapportvaktTabPath(id) {
  const route = RAPPORTVAKT_APP_ROUTES.find((item) => item.id === id);
  return route ? `${RAPPORTVAKT_APP_PATH}/${route.segment}` : RAPPORTVAKT_APP_PATH;
}

/** The route id for a URL segment, or null when the segment is not a route. */
export function rapportvaktTabFromSegment(segment) {
  if (!segment) return RAPPORTVAKT_APP_ROUTES[0].id;
  const route = RAPPORTVAKT_APP_ROUTES.find((item) => item.segment === segment);
  return route ? route.id : null;
}

export const RAPPORTVAKT_TAGLINE =
  'Nya rapporter och statistik, sammanfattade till tre punkter med exakt källhänvisning.';

// Split rather than a plain string constant: this site is prerendered to
// static HTML, so a literal address anywhere in this module would land in
// the crawled page verbatim and get scraped. contactEmail() below is called
// only from an onClick handler (RapportvaktHelpPage.jsx), never printed into
// JSX text or a static href, so the joined address never appears in markup —
// the local part is also split in two (rather than left whole alongside the
// domain) so the full local part never sits contiguously in the *compiled*
// bundle either, which ships to every visitor's browser regardless of what
// gets printed.
export const RAPPORTVAKT_CONTACT_EMAIL_PARTS = ['osama.elda', 'webi', 'gmail.com'];

export function contactEmail() {
  const [localFirst, localSecond, domain] = RAPPORTVAKT_CONTACT_EMAIL_PARTS;
  return `${localFirst}${localSecond}@${domain}`;
}

// Live Stripe payment links (account PUNKTLISTA.SE, SEK, monthly).
export const STRIPE_CHECKOUT_URLS = {
  fler: 'https://buy.stripe.com/28E00lewNdb95GD8II8IU01',
  obegransat: 'https://buy.stripe.com/eVq6oJagx4EDed91gg8IU00',
};

export const RAPPORTVAKT_PRICE = {
  amount: '990 kr',
  period: 'per månad och person',
  periodEn: 'per month per person',
  note: 'Ingen bindningstid.',
  noteEn: 'Cancel anytime.',
};

export const RAPPORTVAKT_TIERS = [
  {
    id: 'fler',
    name: 'Fler',
    nameEn: 'More',
    // PLACEHOLDER PRICE — 490 kr/mån is not a decided price point, it exists so
    // the upgrade sheet has two real tiers to show. Confirm before launch.
    price: '490 kr',
    period: 'per månad',
    periodEn: 'per month',
    limit: '5 källor',
    limitEn: '5 sources',
    lines: [
      'Fem källor du väljer själv',
      'Tre punkter med sidhänvisning per utskick',
      'Utskicket kommer när källan uppdateras',
    ],
    linesEn: [
      'Five sources of your choice',
      'Three points with page references per digest',
      'The digest arrives when the source updates',
    ],
  },
  {
    id: 'obegransat',
    name: 'Obegränsat',
    nameEn: 'Unlimited',
    price: '990 kr',
    period: 'per månad',
    periodEn: 'per month',
    limit: 'Alla källor',
    limitEn: 'All sources',
    lines: [
      'Hela bostadsområdet, alla källor',
      'Tre punkter med sidhänvisning per utskick',
      'Ingen bindningstid',
    ],
    linesEn: [
      'All of Swedish housing, all sources',
      'Three points with page references per digest',
      'Cancel anytime',
    ],
    featured: true,
  },
];

// --- Publisher logos --------------------------------------------------------
//
// Fetched from each publisher's own site (declared apple-touch-icon / icon
// link) and served locally from /logos so no runtime request leaves
// punktlista.se. Used as small identifying marks next to a report, which is
// nominative use; if a publisher objects, drop their file and the UI falls
// back to the two-letter monogram.

export const RAPPORTVAKT_LOGOS = {
  'Hyresgästföreningen': '/logos/hyresgastforeningen.png',
  'Boverket': '/logos/boverket.png',
  'Fastighetsägarna': '/logos/fastighetsagarna.ico',
  'Sveriges Allmännytta': '/logos/sverigesallmannytta.png',
  'SBAB / Booli': '/logos/sbab.png',
  'Länsförsäkringar Fastighetsförmedling': '/logos/lansforsakringar.png',
  'Veidekke': '/logos/veidekke.svg',
  'HSB': '/logos/hsb.png',
  'HSB Göteborg': '/logos/hsb.png',
  'Riksbyggen': '/logos/riksbyggen.png',
  'Byggföretagen': '/logos/byggforetagen.png',
  'Mäklarstatistik': '/logos/maklarstatistik.png',
  'Valueguard': '/logos/valueguard.png',
  'Hemnet Group': '/logos/hemnet.png',
  'SCB': '/logos/scb.png',
  'SEB / Demoskop': '/logos/seb.png',
};

export function publisherLogo(publisher) {
  return RAPPORTVAKT_LOGOS[publisher] || null;
}

// --- Cadence buckets, for filtering the catalogue ---------------------------

export const RAPPORTVAKT_FREQUENCIES = [
  { id: 'vecka', label: 'Varje vecka', labelEn: 'Weekly' },
  { id: 'manad', label: 'Varje månad', labelEn: 'Monthly' },
  { id: 'kvartal', label: 'Kvartalsvis', labelEn: 'Quarterly' },
  { id: 'ar', label: 'Årligen', labelEn: 'Yearly' },
  { id: 'oregelbundet', label: 'Oregelbundet', labelEn: 'Irregular' },
];

export function seriesFrequency(series) {
  const c = series.cadence.toLowerCase();
  if (c.includes('oregelbundet')) return 'oregelbundet';
  if (c.includes('måndag')) return 'vecka';
  if (c.includes('månad')) return 'manad';
  if (c.includes('kvartal')) return 'kvartal';
  return 'ar';
}

// --- Release months, for the year strip on a publisher page ------------------
//
// EDITORIAL CONTRACT, same as the rest of the file: a month is only ever drawn
// because the cadence string names it. "Kvartalsvis" is not four months,
// "första kvartalet" is not January to March and "våren" is not April: none of
// those name a month, so those series get no strip at all and the cadence
// sentence stands on its own. Nothing here may turn a guess into a square.

export const RAPPORTVAKT_MONTHS = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december',
];

export const RAPPORTVAKT_MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The month indexes (0 to 11) a cadence string names outright, or null.
 *
 * "Varje månad" is the one cadence that names every month without listing them,
 * so it answers with all twelve. Everything else is a literal search for the
 * month words; if none are there the answer is null and the caller draws no
 * strip.
 */
export function cadenceMonths(cadence) {
  const text = String(cadence).toLowerCase();
  if (text.includes('varje månad')) return RAPPORTVAKT_MONTHS.map((_, index) => index);
  const named = [];
  RAPPORTVAKT_MONTHS.forEach((month, index) => {
    if (text.includes(month)) named.push(index);
  });
  return named.length ? named : null;
}

// --- Source kinds -------------------------------------------------------
//
// docs/plan/2026-09-13-follow-anything-plan.md, "Vocabulary" and "Supported
// sources, v1": the catalogue unit is a source, and every source has a kind.
// Every entry in RAPPORTVAKT_SERIES below is a 'report' (a PDF report
// series) — the only kind the catalogue carries today — but the filter row
// and the row glyph are written against the full list so a future 'news' or
// 'video' source needs no UI change, only a new catalogue entry. `glyph` is
// the name passed to <Glyph> (see Glyph.jsx); 'newsletter' reuses the
// existing envelope glyph ('mail') rather than a near-identical new one.
export const RAPPORTVAKT_KINDS = [
  { id: 'report', label: 'Rapport', labelEn: 'Report', glyph: 'report' },
  { id: 'news', label: 'Nyheter', labelEn: 'News', glyph: 'news' },
  { id: 'blog', label: 'Blogg', labelEn: 'Blog', glyph: 'blog' },
  { id: 'newsletter', label: 'Nyhetsbrev', labelEn: 'Newsletter', glyph: 'mail' },
  { id: 'video', label: 'Video', labelEn: 'Video', glyph: 'video' },
  { id: 'social', label: 'Socialt', labelEn: 'Social', glyph: 'social' },
  { id: 'page', label: 'Webbsida', labelEn: 'Web page', glyph: 'page' },
];

// --- Upcoming source kinds ------------------------------------------------
//
// Not yet readable by the backend (docs/plan/2026-09-13-follow-anything-
// plan.md, "Supported sources, v1") but shown to readers as "coming soon" on
// the sources tab, so the product's direction is visible without pretending
// these kinds work today. 'newsletter' reuses the existing 'mail' glyph, the
// same choice RAPPORTVAKT_KINDS makes above.
export const RAPPORTVAKT_UPCOMING_KINDS = [
  { id: 'x', label: 'X', labelEn: 'X', glyph: 'x' },
  { id: 'linkedin', label: 'LinkedIn', labelEn: 'LinkedIn', glyph: 'linkedin' },
  { id: 'podcast', label: 'Podd', labelEn: 'Podcast', glyph: 'podcast' },
  { id: 'newsletter', label: 'Nyhetsbrev', labelEn: 'Newsletter', glyph: 'mail' },
];

// --- Watched report series --------------------------------------------------
//
// Source: ~/code/active/rapportvakt/publishers/bostad.md, verified 2026-08-20.
// "channel" is how a release is detectable: 'nyhetsbrev' means the publisher
// announces it themselves, 'webbsida' and 'press' mean they do not.
//
// Defined as RAPPORTVAKT_SERIES_BASE and mapped into RAPPORTVAKT_SERIES below
// so every entry gets 'kind' and 'category' without retyping all 19 rows: the
// whole catalogue is 'report' / 'bostad' today, and a future non-report
// source would be added as its own entry with its own kind rather than
// change this map.
const RAPPORTVAKT_SERIES_BASE = [
  {
    id: 'hgf-lagesrapport',
    publisher: 'Hyresgästföreningen',
    report: 'Hyresgästernas lägesrapport',
    cadence: 'Två gånger per år, vår och höst',
    channel: 'webbsida',
  },
  {
    id: 'hgf-marknadshyra',
    publisher: 'Hyresgästföreningen',
    report: 'Marknadshyresrapporterna',
    cadence: 'Oregelbundet',
    channel: 'webbsida',
  },
  {
    id: 'boverket-bme',
    publisher: 'Boverket',
    report: 'Bostadsmarknadsenkäten',
    cadence: 'Årligen, juni',
    channel: 'webbsida',
  },
  {
    id: 'boverket-byggprognos',
    publisher: 'Boverket',
    report: 'Byggprognoser och indikatorer',
    cadence: 'Två gånger per år, mars och juni',
    channel: 'webbsida',
  },
  {
    id: 'fastighetsagarna-sverigebarometern',
    publisher: 'Fastighetsägarna',
    report: 'Fastighetsägarnas Sverigebarometer',
    cadence: 'Årligen, första kvartalet',
    channel: 'nyhetsbrev',
  },
  {
    id: 'allmannyttan-ekonomi',
    publisher: 'Sveriges Allmännytta',
    report: 'Det ekonomiska läget i allmännyttan',
    cadence: 'Årligen, juni till juli',
    channel: 'nyhetsbrev',
  },
  {
    id: 'sbab-hpi',
    publisher: 'SBAB / Booli',
    report: 'Housing Price Index (HPI)',
    cadence: 'Varje månad',
    channel: 'nyhetsbrev',
  },
  {
    id: 'sbab-hmi',
    publisher: 'SBAB / Booli',
    report: 'Housing Market Index (HMI)',
    cadence: 'Kvartalsvis',
    channel: 'nyhetsbrev',
  },
  {
    id: 'sbab-boprisindikator',
    publisher: 'SBAB / Booli',
    report: 'Boprisindikator',
    cadence: 'Kvartalsvis',
    channel: 'nyhetsbrev',
  },
  {
    id: 'lf-bopanelen',
    publisher: 'Länsförsäkringar Fastighetsförmedling',
    report: 'Mäklarenkäten Bopanelen',
    cadence: 'Två gånger per år',
    channel: 'press',
  },
  {
    id: 'veidekke-byggmarknadsbarometer',
    publisher: 'Veidekke',
    report: 'Veidekkes Byggmarknadsbarometer',
    cadence: 'Oregelbundet, ungefär halvårsvis',
    channel: 'webbsida',
  },
  {
    id: 'hsb-barometern',
    publisher: 'HSB',
    report: 'Sveriges Bostadsrättsbarometer',
    cadence: 'Årligen, våren',
    channel: 'webbsida',
  },
  {
    id: 'riksbyggen-kvadratmeterindex',
    publisher: 'Riksbyggen',
    report: 'Kvadratmeterindex',
    cadence: 'Årligen, sommaren',
    channel: 'press',
  },
  {
    id: 'byggforetagen-byggkonjunkturen',
    publisher: 'Byggföretagen',
    report: 'Byggkonjunkturen',
    cadence: 'Två gånger per år, mars och oktober',
    channel: 'nyhetsbrev',
  },
  {
    id: 'maklarstatistik-manad',
    publisher: 'Mäklarstatistik',
    report: 'Månatlig bostadsprisstatistik',
    cadence: 'Varje månad, omkring den sjunde',
    channel: 'nyhetsbrev',
  },
  {
    id: 'valueguard-hox',
    publisher: 'Valueguard',
    report: 'HOX Prisindex',
    cadence: 'Varje månad',
    channel: 'webbsida',
  },
  {
    id: 'hemnet-marknadsrapport',
    publisher: 'Hemnet Group',
    report: 'Marknadsrapport, regional bostadsmarknad',
    cadence: 'Oregelbundet, ungefär kvartalsvis',
    channel: 'press',
  },
  {
    id: 'scb-nybyggnation',
    publisher: 'SCB',
    report: 'Påbörjad nybyggnation av bostadslägenheter',
    cadence: 'Kvartalsvis',
    channel: 'webbsida',
  },
  {
    id: 'scb-veckans-statistik',
    publisher: 'SCB',
    report: 'Veckans statistiknyheter',
    cadence: 'Varje måndag',
    channel: 'webbsida',
  },
  {
    id: 'seb-boprisindikatorn',
    publisher: 'SEB / Demoskop',
    report: 'Boprisindikatorn',
    cadence: 'Varje månad',
    channel: 'webbsida',
  },
];

export const RAPPORTVAKT_SERIES = RAPPORTVAKT_SERIES_BASE.map((series) => ({
  ...series,
  kind: 'report',
  category: 'bostad',
}));

export const RAPPORTVAKT_CHANNEL_LABELS = {
  nyhetsbrev: 'Eget nyhetsbrev',
  webbsida: 'Bara en webbsida',
  press: 'Pressutskick',
};

export const RAPPORTVAKT_CHANNEL_LABELS_EN = {
  nyhetsbrev: 'Newsletter',
  webbsida: 'Web page only',
  press: 'Press release',
};

// --- Publishers -------------------------------------------------------------
//
// One record per avsändare in RAPPORTVAKT_SERIES, in catalogue order. These back
// /utgivare and /utgivare/:slug, which are the explorative layer: a reader
// who arrives on "Boverket rapporter" should land on something that says
// what Boverket is and what of theirs we watch.
//
// EDITORIAL CONTRACT, same as everywhere else in this file:
//   description    — one factual sentence about what the organisation is. No
//                    superlatives, no market claims, nothing that would need a
//                    source. If it cannot be said plainly it is not said.
//   publicationUrl — copied verbatim from ~/code/active/rapportvakt/publishers/
//                    bostad.md, the row for that publisher's first series. It is
//                    a page where the report lives, which is why the UI calls it
//                    "Rapportsidan" rather than claiming it is a press room.

export const RAPPORTVAKT_PUBLISHERS = [
  {
    slug: 'hyresgastforeningen',
    name: 'Hyresgästföreningen',
    description:
      'Hyresgästföreningen är en medlemsorganisation för hyresgäster som förhandlar hyror för sina medlemmar.',
    publicationUrl:
      'https://www.hyresgastforeningen.se/bostadsfakta/rapporter/hyresgasternas-lagesrapport/',
  },
  {
    slug: 'boverket',
    name: 'Boverket',
    description: 'Boverket är myndigheten för samhällsplanering, byggande och boende.',
    publicationUrl: 'https://www.boverket.se/bme',
  },
  {
    slug: 'fastighetsagarna',
    name: 'Fastighetsägarna',
    description:
      'Fastighetsägarna är branschorganisationen för fastighetsägare och fastighetsbolag i Sverige.',
    publicationUrl:
      'https://www.fastighetsagarna.se/aktuellt/nyheter/2026/sverige/fastighetsagarnas-sverigebarometer-2026-forvantningarna-vaxlar-upp/',
  },
  {
    slug: 'sveriges-allmannytta',
    name: 'Sveriges Allmännytta',
    description:
      'Sveriges Allmännytta är branschorganisationen för allmännyttiga bostadsföretag.',
    publicationUrl:
      'https://www.sverigesallmannytta.se/trycksaker/det-ekonomiska-laget-i-allmannyttan-ekonomisk-statistik-2025',
  },
  {
    slug: 'sbab-booli',
    name: 'SBAB / Booli',
    description:
      'SBAB är en statligt ägd bank inriktad på bolån och äger bostadssajten Booli, som statistiken bygger på.',
    publicationUrl: 'https://www.sbab.se/1/om_sbab/omvarld__analyser.html',
  },
  {
    slug: 'lansforsakringar-fastighetsformedling',
    name: 'Länsförsäkringar Fastighetsförmedling',
    description:
      'Länsförsäkringar Fastighetsförmedling är en rikstäckande mäklarkedja inom Länsförsäkringsgruppen.',
    publicationUrl:
      'https://via.tt.se/pressmeddelande/4186370/maklarenkaten-bopanelen-visar-hogre-fart-pa-bostadsmarknaden-vantar-2026?publisherId=3235722&lang=sv',
  },
  {
    slug: 'veidekke',
    name: 'Veidekke',
    description:
      'Veidekke är ett norskt bygg- och anläggningsföretag med verksamhet i Sverige.',
    publicationUrl:
      'https://www.veidekke.se/aktuellt/pressmeddelanden/veidekkes-byggmarknadsbarometer/',
  },
  {
    slug: 'hsb',
    name: 'HSB',
    description:
      'HSB är en kooperativ bostadsorganisation som bygger och förvaltar bostadsrätter.',
    publicationUrl: 'https://www.hsb.se/nyheter-och-tips/nyheter/undersokningar-och-rapporter/',
  },
  {
    slug: 'riksbyggen',
    name: 'Riksbyggen',
    description:
      'Riksbyggen är ett kooperativt företag som bygger, förvaltar och utvecklar bostäder.',
    publicationUrl: 'https://www.riksbyggen.se/om-riksbyggen/pressrum/2026/3454411/',
  },
  {
    slug: 'byggforetagen',
    name: 'Byggföretagen',
    description:
      'Byggföretagen är bransch- och arbetsgivarorganisationen för bygg-, anläggnings- och specialföretag.',
    publicationUrl: 'https://byggforetagen.se/statistik/byggkonjunkturen-20261/',
  },
  {
    slug: 'maklarstatistik',
    name: 'Mäklarstatistik',
    description:
      'Svensk Mäklarstatistik sammanställer prisstatistik från landets fastighetsmäklare.',
    publicationUrl: 'https://www.maklarstatistik.se/',
  },
  {
    slug: 'valueguard',
    name: 'Valueguard',
    description:
      'Valueguard är ett analysföretag som beräknar prisindex för svenska bostäder.',
    publicationUrl: 'https://valueguard.se/erbjudande/valueguard-hox-prisindex/',
  },
  {
    slug: 'hemnet-group',
    name: 'Hemnet Group',
    description: 'Hemnet Group är bolaget bakom bostadssajten Hemnet.',
    publicationUrl:
      'https://www.hemnetgroup.se/media/nyheter/2026/stora-regionala-skillnader-i-hur-bostadsmarknaden-utvecklas/',
  },
  {
    slug: 'scb',
    name: 'SCB',
    description: 'Statistiska centralbyrån är myndigheten för Sveriges officiella statistik.',
    publicationUrl:
      'https://www.scb.se/hitta-statistik/statistik-efter-amne/boende-bebyggelse-och-mark/byggande-och-ombyggnad/bygglov-nybyggnad-och-ombyggnad/',
  },
  {
    slug: 'seb-demoskop',
    name: 'SEB / Demoskop',
    description:
      'SEB är en svensk bank och undersökningsföretaget Demoskop genomför mätningen åt banken.',
    publicationUrl: 'https://demoskop.se/boprisindikatorn-maj-2026-2/',
  },
];

const PUBLISHER_BY_NAME = new Map(RAPPORTVAKT_PUBLISHERS.map((p) => [p.name, p]));
const PUBLISHER_BY_SLUG = new Map(RAPPORTVAKT_PUBLISHERS.map((p) => [p.slug, p]));

// Trailing slash for the same reason as RAPPORTVAKT_HELP_PATH above: this
// resolves to a prerendered dist/utgivare/<slug>/index.html.
export function rapportvaktPublisherPath(slug) {
  return `/utgivare/${slug}/`;
}

export const RAPPORTVAKT_PUBLISHERS_PATH = '/utgivare/';

export function getPublisherBySlug(slug) {
  return PUBLISHER_BY_SLUG.get(slug) || null;
}

/**
 * The publisher record behind a series or delivery, by its display name.
 *
 * Deliveries name their publisher more precisely than the catalogue does
 * ("HSB Göteborg" published the barometer, the series belongs to "HSB"), so an
 * unknown name is a legitimate miss and returns null rather than throwing.
 */
export function getPublisherByName(name) {
  return PUBLISHER_BY_NAME.get(name) || null;
}

/** The watched series for one publisher, in catalogue order. */
export function seriesForPublisher(publisher) {
  return RAPPORTVAKT_SERIES.filter((series) => series.publisher === publisher.name);
}

export const RAPPORTVAKT_SOURCE_COUNT = RAPPORTVAKT_SERIES.length;

export const RAPPORTVAKT_PUBLISHER_COUNT = new Set(
  RAPPORTVAKT_SERIES.map((series) => series.publisher)
).size;

export const RAPPORTVAKT_SELF_ANNOUNCING_COUNT = RAPPORTVAKT_SERIES.filter(
  (series) => series.channel === 'nyhetsbrev'
).length;

export const RAPPORTVAKT_SILENT_COUNT =
  RAPPORTVAKT_SOURCE_COUNT - RAPPORTVAKT_SELF_ANNOUNCING_COUNT;

/** The series grouped by publisher, in catalogue order. */
export function groupSeriesByPublisher() {
  const groups = [];
  const index = new Map();
  for (const series of RAPPORTVAKT_SERIES) {
    if (!index.has(series.publisher)) {
      const group = { publisher: series.publisher, series: [] };
      index.set(series.publisher, group);
      groups.push(group);
    }
    index.get(series.publisher).series.push(series);
  }
  return groups;
}

// --- Deliveries -------------------------------------------------------------

const HSB_PDF =
  'https://www.hsb.se/globalassets/regionalt-innehall/hsb-goteborg/media/dokument/dokument-per-omrade/nyheter/nyheter-2026/sveriges-bostadsrattsbarometer-2026_final.pdf';
const BYGG_PDF =
  'https://byggforetagen.se/app/uploads/2026/03/Byggkonjunkturen1_2026.pdf';
const HGF_PDF =
  'https://www.hyresgastforeningen.se/globalassets/bostadsfakta/rapporter/2025/lagesrapport/hyresgasternas-lagesrapport-hosten-2025.pdf';

export const RAPPORTVAKT_DELIVERIES = [
  {
    id: 'hsb-bostadsrattsbarometern',
    slug: 'hsb-bostadsrattsbarometern',
    publisher: 'HSB Göteborg',
    publisherShort: 'HSB',
    seriesId: 'hsb-barometern',
    title: 'Sveriges bostadsrättsbarometer 2026',
    subtitle: 'En årlig rapport om bostadsrättsföreningars ekonomi',
    dateLabel: '17 juni 2026',
    dateIso: '2026-06-17',
    sortKey: '2026-06-17',
    pdfUrl: HSB_PDF,
    reportPageUrl:
      'https://www.hsb.se/nyheter-och-tips/nyheter/2026/sveriges-bostadsrattsbarometer-2026/',
    scope:
      'Underlaget är 4 274 bostadsrättsföreningar med 320 550 lägenheter i 207 av landets 290 kommuner, samtliga med ekonomisk förvaltning hos HSB. Siffrorna avser 2025.',
    source:
      'HSB Göteborg (2026). Sveriges bostadsrättsbarometer 2026. En årlig rapport om bostadsrättsföreningars ekonomi. Publicerad 17 juni 2026, 9 sidor i pdf:en i uppslagsformat med tryckta sidnummer 1 till 15. Kontaktperson i rapporten: Cecilia Jutell, analys och rådgivning. Nytt för i år är en regional jämförelse av 17 av Sveriges största städer.',
    metaTitle: 'Sveriges bostadsrättsbarometer 2026 från HSB',
    metaDescription:
      'Tre punkter ur Sveriges bostadsrättsbarometer 2026 från HSB: sparande, vattenkostnad och räntekostnad, med sidnummer och pdf-länk.',
    points: [
      {
        id: 'hsb-1',
        impact: 'Dagens slitage skickas vidare till framtida medlemmar',
        lead:
          'föreningarna sparar i snitt 225 kr per kvadratmeter och år till framtida underhåll, mot de omkring 300 kr som HSB och branschen rekommenderar.',
        citationShort: 'Sidorna 5 och 7, avsnitten om sparande',
        headline: 'Sparandet når inte branschens egen nivå',
        verdict: { tone: 'under', text: 'Under rekommendationen' },
        hero: {
          value: '225',
          unit: 'kr/m² och år',
          caption: 'sparande till framtida underhåll, 2025',
        },
        visual: {
          kind: 'gap',
          title: 'Sparande mot rekommenderad nivå',
          max: 340,
          value: 225,
          valueLabel: '225 kr',
          valueName: 'Faktiskt sparande 2025',
          target: 300,
          targetLabel: 'omkring 300 kr',
          targetName: 'Rekommenderat av HSB och branschen',
          gapNote: 'Under rekommendationen',
        },
        facts: [
          { label: 'På ett år', value: '12,5 %', dir: 'up' },
          { label: 'På fem år', value: 'knappt 1 %', dir: 'up' },
          { label: 'Sparar under 150 kr', value: 'ungefär 600 föreningar' },
          { label: 'Sparar under 100 kr', value: 'omkring 400 föreningar' },
        ],
        detail:
          'Uppgången på 12,5 procent gäller det senaste året. Sett över fem år är sparandet i princip oförändrat. Rapporten skriver att föreningar som sparar under rekommenderad nivå skickar dagens slitage vidare till föreningens framtida medlemmar.',
        quote:
          'HSBs och branschens rekommendation är ett sparande om cirka 300 kr/kvm/år. Siffrorna i denna rapport visar att cirka 600 föreningar har ett sparande under 150 kr/kvm/år, och cirka 400 föreningar under 100 kr/kvm/år.',
        citation: {
          pages: 's. 5 och 7 i pdf:en, uppslag med tryckta sidorna 8–9 och 12–13',
          sections: [
            'Sparande (till framtida underhåll)',
            'Trendbrott: Högsta sparandet på fem år',
          ],
          url: `${HSB_PDF}#page=7`,
          linkLabel: 'Öppna pdf:en på s. 7',
        },
      },
      {
        id: 'hsb-2',
        impact: 'Det är kommunerna, inte inflationen, som driver upp föreningarnas kostnader',
        lead:
          'vattenkostnaden har stigit 38 procent på fem år och tomträttsavgälden drygt 22 procent, mot drygt 13 procent för driften som helhet.',
        citationShort: 'Sidorna 4, 5 och 7, avsnitten om driftkostnader',
        headline: 'Kommunala taxor driver upp driften',
        verdict: { tone: 'up', text: 'Snabbare än driftkostnaden i stort' },
        hero: {
          value: '38 %',
          unit: 'på fem år',
          caption: 'vattenkostnaden, nu 51 kr/m² och år',
        },
        visual: {
          kind: 'bars',
          title: 'Förändring på fem år',
          max: 40,
          rows: [
            {
              label: 'Vatten',
              value: 38,
              valueLabel: '38 %',
              note: '51 kr/m² och år',
              role: 'actual',
            },
            {
              label: 'Tomträttsavgäld',
              value: 22,
              valueLabel: 'drygt 22 %',
              note: '77 kr/m²',
              role: 'actual',
            },
            {
              label: 'Avfall',
              value: 19,
              valueLabel: '19 %',
              role: 'actual',
            },
            {
              label: 'Driftkostnad totalt',
              value: 13,
              valueLabel: 'drygt 13 %',
              note: '535 kr/m²',
              role: 'ref',
            },
          ],
          legend: [
            { role: 'actual', label: 'Kommunal taxa' },
            { role: 'ref', label: 'Driften som helhet' },
          ],
        },
        facts: [
          { label: 'Vatten på ett år', value: 'drygt 13 %', dir: 'up' },
          { label: 'Driftkostnad 2025', value: '535 kr/m²' },
        ],
        detail:
          'Driftkostnaden avser posten exklusive tomträttsavgäld. HSB skriver rakt ut att föreningarna behöver vänja sig vid att driftkostnader och kommunala taxor kommer att öka mer än inflationen framöver.',
        quote:
          'Tiderna där den klassiska avgiftsindexeringen på 2 procent per år räcker, är förbi. Bostadsrättsföreningar behöver vänja sig vid att driftkostnader och kommunala taxor kommer att öka mer än inflationen framöver.',
        citation: {
          pages: 's. 4, 5 och 7 i pdf:en, tryckta sidorna 6–7, 8–9 och 12–13',
          sections: [
            'Driftkostnader (exkl tomträttsavgäld)',
            'Vatten',
            'Avfall',
            'Tomträttsavgäld',
            'Driftkostnader ökar mer än inflationen',
          ],
          url: `${HSB_PDF}#page=4`,
          linkLabel: 'Öppna pdf:en på s. 4',
        },
      },
      {
        id: 'hsb-3',
        impact: 'Räntan är fortfarande föreningarnas största sårbarhet',
        lead:
          'räntekostnaden sjönk till 137 kr per kvadratmeter och år, men ligger 154 procent högre än för fem år sedan och står för nästan 20 procent av de löpande kostnaderna.',
        citationShort: 'Sidorna 5, 6 och 7, avsnitten om skuld och ränta',
        headline: 'Räntan är föreningarnas största sårbarhet',
        verdict: { tone: 'down', text: 'Ned på ett år, upp på fem' },
        hero: {
          value: '137',
          unit: 'kr/m² och år',
          caption: 'räntekostnad 2025',
        },
        visual: {
          kind: 'delta',
          title: 'Räntekostnaden jämfört med',
          items: [
            { period: 'Året innan', value: 'drygt 2 %', word: 'lägre', dir: 'down' },
            { period: 'Fem år sedan', value: '154 %', word: 'högre', dir: 'up' },
          ],
        },
        facts: [
          { label: 'Andel av löpande kostnader', value: 'nästan 20 %' },
          { label: 'Räntekänslighet', value: '5,9 %' },
          { label: 'Skuldsättning', value: '4 766 kr/m²', dir: 'up', note: 'upp 2,8 % på ett år' },
        ],
        detail:
          'Räntekänsligheten på 5,9 procent är den avgiftshöjning som krävs för att täcka räntekostnaden om lånens genomsnittsränta ökar med 1 procent. Skuldsättningen steg samtidigt med 2,8 procent till 4 766 kronor per kvadratmeter.',
        quote:
          'Under 2025 var kostnaden i genomsnitt 137 kr/kvm/år, vilket är en minskning med drygt 2 procent från året innan. Den senaste femårsperioden har den genomsnittliga räntekostnaden ökat med 154 procent.',
        citation: {
          pages: 's. 5, 6 och 7 i pdf:en, tryckta sidorna 8–9, 10–11 och 12–13',
          sections: [
            'Skuldsättning',
            'Räntekostnad',
            'Räntekänslighet',
            'Räntan vänder tillbaka – så även räntekostnaderna',
          ],
          url: `${HSB_PDF}#page=6`,
          linkLabel: 'Öppna pdf:en på s. 6',
        },
      },
    ],
  },
  {
    id: 'byggforetagen-byggkonjunkturen',
    slug: 'byggforetagen-byggkonjunkturen',
    publisher: 'Byggföretagen',
    publisherShort: 'Byggföretagen',
    seriesId: 'byggforetagen-byggkonjunkturen',
    title: 'Byggkonjunkturen 2026:1',
    subtitle: 'Konjunkturrapport mars 2026',
    dateLabel: 'mars 2026',
    dateIso: '2026-03-31',
    sortKey: '2026-03-31',
    pdfUrl: BYGG_PDF,
    reportPageUrl:
      'https://byggforetagen.se/statistik/konjunktur-prognos/byggkonjunkturen-20261/',
    scope:
      'Sidhänvisningarna avser pdf-sidan. Rapportens tryckta sidnummer ligger ett steg lägre.',
    source:
      'Byggföretagen Analys (2026). Byggkonjunkturen 2026:1. Konjunkturrapport mars 2026. Utges av Byggföretagen Analys, 32 sidor i pdf:en. Redaktion: Johan Deremar (prognosansvarig), Emil Flodin (analytiker), Fredrik Isaksson (chefekonom) och Anna Broman (näringspolitisk expert).',
    metaTitle: 'Byggkonjunkturen 2026:1 från Byggföretagen',
    metaDescription:
      'Tre punkter ur Byggkonjunkturen 2026:1 från Byggföretagen: påbörjade bostäder, bolånetak och hyresrätter, med sidnummer och pdf-länk.',
    points: [
      {
        id: 'bygg-1',
        impact: 'Återhämtningen i byggandet går långsammare än rubrikerna antyder',
        lead:
          '30 200 bostäder påbörjades under 2025, och 2000-talets genomsnitt på 36 000 per år nås enligt prognosen först 2027.',
        citationShort: 'Sidorna 5 och 6, avsnittet Bostäder',
        headline: 'Snittet för 2000-talet nås först 2027',
        verdict: { tone: 'under', text: 'Under 2000-talets genomsnitt' },
        hero: {
          value: '30 200',
          unit: 'bostäder',
          caption: 'påbörjades under 2025',
        },
        visual: {
          kind: 'columns',
          title: 'Påbörjade bostäder per år',
          max: 72000,
          benchmark: { value: 36000, label: '2000-talets genomsnitt, 36 000' },
          columns: [
            { label: '2021', value: 68000, valueLabel: '68 000', role: 'past' },
            { label: '2025', value: 30200, valueLabel: '30 200', role: 'actual' },
            { label: '2026', value: 33500, valueLabel: '33 500', role: 'forecast', tag: 'prognos' },
            { label: '2027', value: 35900, valueLabel: '35 900', role: 'forecast', tag: 'prognos' },
          ],
        },
        facts: [
          { label: 'Flerbostadshus 2025', value: '24 300' },
          { label: 'Småhus 2025', value: '5 900' },
          { label: 'Nybyggnadsinvesteringar av BNP', value: 'lägsta på 25 år' },
        ],
        detail:
          'Prognosen ligger på 33 500 påbörjade lägenheter i år och 35 900 nästa år. Det innebär att 2000-talets genomsnitt på 36 000 bostäder per år nås först 2027. I förhållande till BNP ligger nybyggnadsinvesteringarna kvar på den lägsta nivån på 25 år.',
        quote:
          'Under 2025 påbörjades 30.200 nya bostäder, varav 24.300 i flerbostadshus och 5.900 småhus (enligt Byggföretagens uppräkning av den preliminära statistiken). Detta är en försiktig inledning på en återhämtning efter den historiskt dramatiska nedgången från de 68.000 byggstarterna 2021.',
        citation: {
          pages: 's. 5–6 i pdf:en, tryckt s. 4–5',
          sections: ['Bostäder'],
          url: `${BYGG_PDF}#page=5`,
          linkLabel: 'Öppna pdf:en på s. 5',
        },
      },
      {
        id: 'bygg-2',
        impact: 'Halva kreditreformen är beslutad, andra halvan saknar politisk ambition',
        lead:
          'bolånetaket höjs till 90 procent och det skärpta amorteringskravet avskaffas 1 april, men full utväxling på byggandet kräver enligt rapporten ett andra steg.',
        citationShort: 'Sidan 7, avsnittet Bostäder',
        headline: 'Kreditlättnaden räcker inte hela vägen',
        verdict: { tone: 'up', text: 'Steg ett beslutat, steg två saknas' },
        hero: {
          from: '85 %',
          value: '90 %',
          unit: 'av bostadens värde',
          caption: 'nytt bolånetak från 1 april',
        },
        visual: {
          kind: 'steps',
          title: 'Vad som krävs för full utväxling',
          steps: [
            {
              state: 'done',
              label: 'Steg 1',
              status: 'Beslutat, 1 april',
              lines: [
                'Det skärpta amorteringskravet avskaffas',
                'Bolånetaket höjs från 85 till 90 procent av bostadens värde',
              ],
            },
            {
              state: 'missing',
              label: 'Steg 2',
              status: 'Saknas',
              lines: [
                'Amorteringstakten sänks med en procentenhet för lån över 50 procent av marknadsvärdet',
              ],
              note: 'Full utväxling på nyproduktionsvolymerna kommer först efter det här steget.',
            },
          ],
        },
        facts: [
          { label: 'Beräkningarna', value: 'Evidens, på uppdrag av Byggföretagen' },
          { label: 'Politisk ambition för steg 2', value: 'saknas' },
        ],
        detail:
          'Beräkningarna är gjorda av analysföretaget Evidens på uppdrag av Byggföretagen. Rapporten konstaterar att den politiska ambitionen att genomföra det andra steget saknas.',
        quote:
          'Beräkningar som analysföretaget Evidens gjort på uppdrag av Byggföretagen visar att det är efter genomförande av detta andra steg som vi kan förvänta oss full utväxling på nyproduktionsvolymerna. Här saknas dessvärre den politiska ambitionen att gå vidare med att genomföra detta andra steg varför utvecklingen riskerar att hämmas.',
        citation: {
          pages: 's. 7 i pdf:en, tryckt s. 6',
          sections: ['Bostäder'],
          url: `${BYGG_PDF}#page=7`,
          linkLabel: 'Öppna pdf:en på s. 7',
        },
      },
      {
        id: 'bygg-3',
        impact: 'Hyresrättssiffran är svagare än den ser ut',
        lead:
          '16 900 hyresrätter påbörjas i år mot 10 000 bostadsrätter enligt prognosen, men nivån förklaras av en stor andel specialbostäder.',
        citationShort: 'Sidan 8, avsnittet Bostäder',
        headline: 'Hyresrätterna håller uppe hela prognosen',
        verdict: { tone: 'up', text: 'Men på specialbostäder' },
        hero: {
          value: '16 900',
          unit: 'hyresrätter',
          caption: 'påbörjas i år enligt prognosen',
        },
        visual: {
          kind: 'columns',
          title: 'Prognos per upplåtelseform',
          max: 19000,
          series: [
            { role: 'actual', label: 'Hyresrätter' },
            { role: 'ref', label: 'Bostadsrätter' },
          ],
          groups: [
            {
              label: 'I år',
              values: [
                { value: 16900, valueLabel: '16 900', role: 'actual' },
                { value: 10000, valueLabel: '10 000', role: 'ref' },
              ],
            },
            {
              label: 'Nästa år',
              values: [
                { value: 17100, valueLabel: '17 100', role: 'actual' },
                { value: 10800, valueLabel: '10 800', role: 'ref' },
              ],
            },
          ],
        },
        facts: [
          { label: '2006 till 2016', value: 'i det närmaste lika många av varje' },
          { label: 'Glappet uppstod', value: 'efter det skärpta amorteringskravet 2017' },
        ],
        detail:
          'Byggföretagen förklarar nivån med en stor andel specialbostäder, bland annat studentbostäder och specialbostäder för äldre och funktionshindrade. De byggregler som från 1 juli 2025 tillåter mindre boarea för studentbostäder gör i sig att antalet lägenheter ökar.',
        quote:
          'Att hyresrättsbyggandet kunnat bibehållas på relativt hög nivå förklaras på senare tid bl.a. av en stor del specialbostäder. Det rör sig om exempelvis, studentbostäder, specialbostäder för äldre och funktionshindrade mm.',
        citation: {
          pages: 's. 8 i pdf:en, tryckt s. 7',
          sections: ['Bostäder'],
          extra:
            'Prognossiffrorna står i tabellen "Nyproduktion av bostäder per hustyp och upplåtelseform" på s. 6 i pdf:en.',
          url: `${BYGG_PDF}#page=8`,
          linkLabel: 'Öppna pdf:en på s. 8',
        },
      },
    ],
  },
  {
    id: 'hyresgastforeningen-lagesrapport',
    slug: 'hyresgastforeningen-lagesrapport',
    publisher: 'Hyresgästföreningen',
    publisherShort: 'Hyresgästföreningen',
    seriesId: 'hgf-lagesrapport',
    title: 'Hyresgästernas lägesrapport: Hösten 2025',
    subtitle: 'Förändringstryck och stiltje. Uppföljning av småhusbyggande och inkomstkrav',
    dateLabel: 'november 2025',
    dateIso: '2025-11-15',
    sortKey: '2025-11-15',
    pdfUrl: HGF_PDF,
    reportPageUrl:
      'https://www.hyresgastforeningen.se/bostadsfakta/rapporter/hyresgasternas-lagesrapport/',
    scope: 'Kapitel två om inkomstkrav är skrivet av Johanna Scocco.',
    source:
      'Hedman, Sara (2025). Hyresgästernas lägesrapport: Hösten 2025. Förändringstryck och stiltje: Uppföljning av småhusbyggande och inkomstkrav. Hyresgästföreningen Riksförbundet, november 2025, 30 sidor.',
    metaTitle: 'Hyresgästernas lägesrapport hösten 2025',
    metaDescription:
      'Tre punkter ur Hyresgästernas lägesrapport hösten 2025: småhusbyggande, planeringsstimulans och inkomstkrav, med sidnummer och pdf-länk.',
    points: [
      {
        id: 'hgf-1',
        impact: 'Regeringens småhussatsning har ännu inte gett volym',
        lead:
          'kommunernas mest optimistiska prognos för 2026, omkring 9 300 påbörjade småhus, ligger under 2000-talets genomsnitt på omkring 10 000 per år.',
        citationShort: 'Sidan 7, avsnittet om byggprognoserna',
        headline: 'Småhussatsningen syns inte i byggsiffrorna',
        verdict: { tone: 'under', text: 'Under 2000-talets genomsnitt' },
        hero: {
          prefix: 'omkring',
          value: '9 300',
          unit: 'småhus',
          caption: 'kommunernas prognos för 2026, den mest optimistiska siffran',
        },
        visual: {
          kind: 'columns',
          title: 'Påbörjade småhus, prognoser',
          max: 11000,
          benchmark: {
            value: 10000,
            label: '2000-talets genomsnitt, omkring 10 000 per år',
          },
          series: [
            { role: 'ref', label: 'Boverket, juni 2025' },
            { role: 'actual', label: 'Kommunerna, bostadsmarknadsenkäten' },
          ],
          groups: [
            {
              label: '2025',
              values: [
                { value: 6000, valueLabel: '6 000', role: 'ref' },
                { value: 6700, valueLabel: 'cirka 6 700', role: 'actual' },
              ],
            },
            {
              label: '2026',
              values: [
                { value: 6700, valueLabel: '6 700', role: 'ref' },
                { value: 9300, valueLabel: 'omkring 9 300', role: 'actual' },
              ],
            },
          ],
        },
        facts: [
          { label: 'Boverkets prognos', value: 'juni 2025' },
          { label: 'Kommunernas siffror', value: 'bostadsmarknadsenkäten' },
        ],
        detail:
          'Även kommunernas mer optimistiska siffra för 2026 ligger under 2000-talets genomsnitt på omkring 10 000 småhus per år.',
        quote:
          'Byggprognoserna visar att småhusbyggandet inte väntas öka under 2025, utan ligga kvar på samma låga nivå som bottenåret 2024. För 2026 förutspår kommunerna en viss uppgång, men fortfarande under 2000-talets genomsnitt på omkring 10 000 småhus per år.',
        citation: {
          pages: 's. 7',
          sections: ['Försiktiga byggprognoser för 2025–2026'],
          extra: 'Avsnittets rubrik står längst ned på s. 6.',
          url: `${HGF_PDF}#page=7`,
          linkLabel: 'Öppna pdf:en på s. 7',
        },
      },
      {
        id: 'hgf-2',
        impact: 'Stödet som lyft småhusplaneringen är snart slut',
        lead:
          'drygt 65 av årets ungefär 75 miljoner kronor var redan fördelade, och stödet går att söka fram till 1 oktober 2026.',
        citationShort: 'Sidan 8, avsnittet om detaljplaneaktivitet',
        headline: 'Planeringsstimulansen är nästan slut',
        verdict: { tone: 'up', text: 'Högre stöd, mindre pengar kvar' },
        hero: {
          prefix: 'nästan',
          value: '87 %',
          caption: 'av 2025 års anslag var redan fördelat',
          meter: {
            share: 87,
            label: 'drygt 65 miljoner kronor fördelat',
            total: 'av ungefär 75 miljoner kronor för 2025',
          },
        },
        visual: {
          kind: 'bars',
          title: 'Vad utfallet motsvarar i nya småhus',
          max: 4600,
          rows: [
            {
              label: 'I år',
              range: [2600, 4300],
              valueLabel: '2 600 till 4 300',
              role: 'actual',
            },
            {
              label: 'Hela 2024',
              range: [1300, 1400],
              valueLabel: 'ungefär 1 300 till 1 400',
              role: 'ref',
            },
          ],
        },
        facts: [
          { label: 'Anslag 2024', value: '60 miljoner kr' },
          { label: 'Anslag 2025', value: 'ungefär 75 miljoner kr', dir: 'up' },
          { label: 'Stöd per småhus', value: '15 000 → 25 000 kr', dir: 'up' },
          { label: 'Går att söka till', value: '1 oktober 2026' },
        ],
        detail:
          'Stödnivån höjdes från 15 000 till 25 000 kronor per småhus den 1 april 2025. Anslaget växte från 60 miljoner kronor 2024 till ungefär 75 miljoner för 2025, och drygt 65 miljoner var redan fördelat. Stödet går att söka fram till 1 oktober 2026.',
        quote:
          'Hittills i år har drygt 65 miljoner fördelats, motsvarande nästan 87 procent av de avsatta 75 miljonerna för år 2025. Beroende av stödnivå motsvarar det mellan 2 600–4 300 nya småhus.',
        citation: {
          pages: 's. 8',
          sections: ['Tendenser till ökad detaljplaneaktivitet i vissa delar i landet'],
          url: `${HGF_PDF}#page=8`,
          linkLabel: 'Öppna pdf:en på s. 8',
        },
      },
      {
        id: 'hgf-3',
        impact: 'Bilden av en våg av sänkta inkomstkrav håller inte',
        lead:
          'av de 35 allmännyttiga bolagen i landets 30 största kommuner har 80 procent, viktat mot storlek, inte ändrat sina huvudsakliga inkomstkrav på fem år.',
        citationShort: 'Sidorna 20 och 21, avsnittet om inkomstkraven',
        headline: 'Ingen våg av sänkta inkomstkrav',
        verdict: { tone: 'flat', text: 'Oförändrat i fem år' },
        hero: {
          value: '80 %',
          caption: 'har inte ändrat sina huvudsakliga inkomstkrav på fem år',
        },
        visual: {
          kind: 'split',
          title: 'De 35 allmännyttiga bolagen',
          rows: [
            {
              label: 'Oförändrade huvudsakliga inkomstkrav',
              share: 80,
              valueLabel: '80 %',
              note: 'viktat mot storlek',
              role: 'actual',
            },
            {
              label: 'Sänkt gränsen eller godtar fler inkomstslag',
              share: 15,
              valueLabel: '15 %',
              note: 'viktat mot storlek',
              role: 'ref',
            },
            {
              label: 'Infört särskilda och strängare krav för delar av beståndet',
              share: 26,
              valueLabel: '26 %',
              note: 'viktat mot innehav',
              role: 'counter',
            },
          ],
          footnote:
            'Den sista raden mäts på ett annat sätt än de två första och ska inte läggas ihop med dem.',
        },
        facts: [
          { label: 'Underlag', value: '35 allmännyttiga bolag' },
          { label: 'Geografi', value: 'landets 30 största kommuner' },
        ],
        detail:
          'De strängare kraven gäller delar av beståndet, i vissa områden eller i nyproduktion. Andelen är viktad mot innehav, medan de två första andelarna är viktade mot bolagens storlek.',
        quote:
          'Åtta bolag anger att de har ändrat sina huvudsakliga inkomstkrav de senaste fem åren, vilket viktat mot storlek utgör 20 procent (se Figur 11).',
        citation: {
          pages: 's. 20–21',
          sections: ['Förändringar i inkomstkraven'],
          url: `${HGF_PDF}#page=20`,
          linkLabel: 'Öppna pdf:en på s. 20',
        },
      },
    ],
  },
];

/** Newest first. The data is authored in that order; this keeps it honest. */
export const RAPPORTVAKT_DELIVERIES_SORTED = [...RAPPORTVAKT_DELIVERIES].sort((a, b) =>
  a.sortKey < b.sortKey ? 1 : a.sortKey > b.sortKey ? -1 : 0
);

export function getDeliveryBySlug(slug) {
  return RAPPORTVAKT_DELIVERIES.find((delivery) => delivery.slug === slug) || null;
}

/**
 * Sample deliveries belonging to one publisher, newest first.
 *
 * Matched through seriesId rather than the delivery's own publisher string:
 * "HSB Göteborg" published the barometer, but the watched series belongs to
 * "HSB", and the publisher page has to find it anyway.
 */
export function deliveriesForPublisher(publisher) {
  const seriesIds = new Set(seriesForPublisher(publisher).map((series) => series.id));
  return RAPPORTVAKT_DELIVERIES_SORTED.filter((delivery) => seriesIds.has(delivery.seriesId));
}

// Trailing slash for the same reason as RAPPORTVAKT_HELP_PATH above: this
// resolves to a prerendered dist/exempel/<slug>/index.html.
export function rapportvaktSamplePath(slug) {
  return `/exempel/${slug}/`;
}

/**
 * Public sample pages. A cold outreach email already links to
 * /exempel/hyresgastforeningen-lagesrapport (formerly
 * /rapportvakt/exempel/hyresgastforeningen-lagesrapport under
 * utforskadata.se — a redirect from the old path will be needed once this is
 * deployed), so these slugs are a contract: they must keep resolving and stay
 * indexable.
 */
export const RAPPORTVAKT_SAMPLES = RAPPORTVAKT_DELIVERIES.map((delivery) => ({
  slug: delivery.slug,
  title: delivery.metaTitle,
  metaDescription: delivery.metaDescription,
}));

/** The delivery shown as proof on the landing page. */
export const RAPPORTVAKT_PROOF_SLUG = 'hyresgastforeningen-lagesrapport';

export const RAPPORTVAKT_STEPS = [
  {
    title: 'Vi bevakar utgivarna',
    body: `${RAPPORTVAKT_SOURCE_COUNT} återkommande rapporter och index från ${RAPPORTVAKT_PUBLISHER_COUNT} avsändare inom bostad och samhällsbyggnad. Du behöver inte hålla reda på när något släpps.`,
  },
  {
    title: 'Rapporten läses när den släpps',
    body: 'Vi läser hela rapporten och plockar ut de tre punkter som faktiskt spelar roll för en beslutsfattare. Inte referat, utan det som ändrar en bedömning.',
  },
  {
    title: 'Du får tre punkter med sidnummer',
    body: 'Varje punkt har rubrik, nyckeltal, sidnummer, avsnitt och en direktlänk som öppnar pdf:en på rätt sida. Du kan citera utan att läsa om rapporten.',
  },
];

// --- Digests (automated weekly/daily/monthly rollups) -----------------------
//
// RAPPORTVAKT_DELIVERIES above is hand-authored directly in this file, one
// release at a time. RAPPORTVAKT_DIGESTS is the other kind of content this
// app serves: automated rollups exported from Postgres by
// scripts/export_digests.py into src/config/digests.js. Same EDITORIAL
// CONTRACT as the rest of this file applies to that export — see that
// script's own header — this section only reads the array, it never edits a
// figure, quote or citation on its way to the page.

export { RAPPORTVAKT_DIGESTS };

/**
 * ISO 8601 week number and week-year for a "YYYY-MM-DD" date, Monday-based.
 *
 * Standard "nearest Thursday" algorithm: a date's ISO week is the week that
 * contains that date's Thursday, and the week-year is the year that Thursday
 * falls in — not necessarily the calendar year of the date itself, since the
 * final days of December can land in week 1 of the next year and the first
 * days of January can land in week 52/53 of the previous one. Verified
 * against 2026-08-31, a Monday, which this returns as week 36 of 2026 — the
 * SCB week used throughout this plan.
 */
export function isoWeek(dateIso) {
  const [y, m, d] = dateIso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7; // Monday = 0 ... Sunday = 6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week = 1 + Math.round((date - firstThursday) / (7 * 24 * 3600 * 1000));
  return { week, year: date.getUTCFullYear() };
}

/**
 * The public path for one digest.
 *
 * SCB's weekly rollup gets the readable /scb/vecka-<week>-<year>/ form, week
 * and year derived from windowStart (the Monday the digest's own week
 * starts), not dateIso. Everything else — no other series/frequency
 * combination is exported yet — falls back to a generic /digest/<slug>/ so a
 * future digest is never left with no page.
 *
 * React Router 6 cannot match two params inside one path segment (no
 * "/scb/vecka-:week-:year/"; see RapportvaktDigestPage.jsx), so the app
 * route is "/scb/:weekSlug/" and this is the one place that builds and reads
 * the "vecka-<week>-<year>" segment — RapportvaktDigestPage parses it back
 * with the same shape.
 */
export function rapportvaktDigestPath(digest) {
  if (digest.frequency === 'weekly' && digest.seriesSlug.startsWith('scb-') && digest.windowStart) {
    const { week, year } = isoWeek(digest.windowStart);
    return `/scb/vecka-${week}-${year}/`;
  }
  return `/digest/${digest.slug}/`;
}

export function getDigestBySlug(slug) {
  return RAPPORTVAKT_DIGESTS.find((digest) => digest.slug === slug) || null;
}

/** The SCB weekly digest for a given ISO week/year, or null. */
export function getScbDigestByWeek(week, year) {
  const w = Number(week);
  const y = Number(year);
  return (
    RAPPORTVAKT_DIGESTS.find((digest) => {
      if (digest.frequency !== 'weekly' || !digest.seriesSlug.startsWith('scb-')) return false;
      if (!digest.windowStart) return false;
      const info = isoWeek(digest.windowStart);
      return info.week === w && info.year === y;
    }) || null
  );
}

/** Newest first, matching RAPPORTVAKT_DELIVERIES_SORTED's own contract. */
export const RAPPORTVAKT_DIGESTS_SORTED = [...RAPPORTVAKT_DIGESTS].sort((a, b) =>
  a.windowStart < b.windowStart ? 1 : a.windowStart > b.windowStart ? -1 : 0
);

/** One series' digests, oldest first — the order prev/next walks. */
function digestsForSeries(seriesSlug) {
  return RAPPORTVAKT_DIGESTS.filter((digest) => digest.seriesSlug === seriesSlug).sort((a, b) =>
    a.windowStart < b.windowStart ? -1 : a.windowStart > b.windowStart ? 1 : 0
  );
}

/** The digest for the week before and after this one, in the same series. */
export function adjacentDigests(digest) {
  const series = digestsForSeries(digest.seriesSlug);
  const index = series.findIndex((d) => d.slug === digest.slug);
  return {
    prev: index > 0 ? series[index - 1] : null,
    next: index >= 0 && index < series.length - 1 ? series[index + 1] : null,
  };
}

/**
 * Up to 8 most recent digests for one publisher's series, newest first.
 *
 * Matched through seriesSlug against seriesForPublisher, the same way
 * deliveriesForPublisher matches deliveries above.
 */
export function digestsForPublisher(publisher) {
  const seriesSlugs = new Set(seriesForPublisher(publisher).map((series) => series.id));
  return RAPPORTVAKT_DIGESTS_SORTED.filter((digest) => seriesSlugs.has(digest.seriesSlug)).slice(
    0,
    8
  );
}
